/**
 * Audio Engine Constants
 * Central location for all magic numbers used throughout the audio engine
 */

export const AUDIO_CONSTANTS = {
  // Sample rates and buffer sizes
  SAMPLE_RATE: 48000,
  INITIAL_BUFFER_SECONDS: 10,
  LAZY_LOAD_BUFFER_SECONDS: 3,
  FRAME_SIZE: 4096, // Increased from 1024 for 75% reduction in frame processing overhead
  WORKLET_FRAME_SIZE: 128,

  // Channel configuration
  MAX_CHANNELS: 4,
  CROSSFADE_CHANNELS: 2,
  DEFAULT_CHANNELS: 2,

  // Tempo settings
  DEFAULT_TEMPO: 100,
  MIN_TEMPO: 50,
  MAX_TEMPO: 150,
  TRACK_BPM: 100, // All tracks are recorded at 100 BPM

  // Timing and beats - all derived from individual track metadata
  DEFAULT_TIME_SIGNATURE: 4,
  SECONDS_PER_MINUTE: 60,

  // NOTE: Track lengths use AudioTrackLength interface from individual M4A metadata
  // - Each track has its own trackLength.syncLength from metadata extraction
  // - Beat count calculated from: syncLength / samplesPerBeat
  // - No fallback values needed - metadata extraction is mandatory

  // Audio processing
  DEFAULT_GAIN: 1.0,
  MIN_GAIN: 0.0,
  MAX_GAIN: 2.0,

  // Worker pool
  MAX_WORKERS: 4,
  WORKER_TIMEOUT_MS: 5000,

  // Buffer pool
  DEFAULT_BUFFER_POOL_SIZE: 10,
  MIN_BUFFER_LENGTH: 128,

  // Timeouts
  WORKLET_READY_TIMEOUT_MS: 5000,
  AUDIO_LOAD_TIMEOUT_MS: 30000,
  DEFAULT_CROSSFADE_TIME_MS: 2000,

  // AAC parsing
  AAC_SYNC_WORD: 0xfff,
  AAC_HEADER_LENGTH: 7,
  AAC_MIN_FRAME_SIZE: 7,

  // Alignment
  ALIGNMENT_BYTES: 32,
  CACHE_LINE_SIZE: 64,

  // Performance
  LAZY_LOAD_THRESHOLD: 0.8, // Start loading next chunk at 80% playback
  PRELOAD_BUFFER_COUNT: 2,
  FRAMES_PER_READ: 5, // Reduced from 10 to maintain similar data throughput (5 * 4096 ≈ 10 * 1024)

  // Audio file URLs from Cloudflare R2
  TRACK_URLS: [
    'https://pub-e47598ba1c1049b7acbf143465591420.r2.dev/Drum.m4a',
    'https://pub-e47598ba1c1049b7acbf143465591420.r2.dev/Perc.m4a',
    'https://pub-e47598ba1c1049b7acbf143465591420.r2.dev/Bass.m4a',
    'https://pub-e47598ba1c1049b7acbf143465591420.r2.dev/Guitar.m4a',
    'https://pub-e47598ba1c1049b7acbf143465591420.r2.dev/Pad.m4a',
    'https://pub-e47598ba1c1049b7acbf143465591420.r2.dev/Piano1.m4a',
    'https://pub-e47598ba1c1049b7acbf143465591420.r2.dev/Piano2.m4a'
  ] as const
} as const;

// Type-safe access to constants
export type AudioConstants = typeof AUDIO_CONSTANTS;
